import { isMapId, type MapId } from "@69ly/maps";
import { planClock, type Combo, type Execution, type LiveMember, type PlanAction, type PlanLookup, type PlantSpot, type Schedule, type Site, type Stuff, type Technique as TechniqueType } from "@69ly/shared";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCallouts } from "../../lib/callouts.ts";
import { isPlayer, type PlanActor } from "../../lib/plan.ts";
import { Avatar } from "../Avatar.tsx";
import { ComboGlyphs, ComboMedia, ComboSteps, defaultComboShow, type ComboShow } from "../ComboBits.tsx";
import { Icon } from "../Icon.tsx";
import { useImageViewer } from "../ImageViewer.tsx";
import { GrenadeIcon } from "../MarkerIcon.tsx";
import { beatIds } from "../../lib/beats.ts";
import { BeatBody, beatIcon, useBeats, type PartsContext } from "../plan/PlanParts.tsx";
import { StuffLink } from "../StuffDrawer.tsx";
import { clipPoster, StageMedia, ZoomShot } from "../StuffMedia.tsx";
import { Technique } from "../Technique.tsx";

export type Translate = (k: string, o?: Record<string, unknown>) => string;

/** "Running + jump · Left click": how it is thrown, in a few words. */
export function techniqueText(t: Translate, tech: TechniqueType): string {
  const move = tech.jump ? (tech.movement === "standing" ? t("form.jump") : `${t(`movement.${tech.movement}`)} + ${t("form.jump").toLowerCase()}`) : t(`movement.${tech.movement}`);
  return `${move} · ${t(`click.${tech.click}`)}`;
}

/** The players who throw it: small overlapping faces. */
export function WhoFaces({ ids, members, me, size = 22 }: { ids: string[]; members: LiveMember[]; me: string; size?: number }) {
  const { t } = useTranslation();
  const people = ids.map((id) => members.find((m) => m.id === id)).filter((m): m is LiveMember => !!m);
  if (people.length === 0) return null;
  const names = people.map((m) => (m.id === me ? `${m.name} (${t("live.you")})` : m.name)).join(", ");
  return (
    <span className="live-faces" title={names} aria-label={t("live.thrownBy", { names })}>
      {people.slice(0, 3).map((m) => (
        <Avatar key={m.id} name={m.name} url={m.avatarUrl} size={size} me={m.id === me} />
      ))}
      {people.length > 3 && <span className="live-faces-more">+{people.length - 3}</span>}
    </span>
  );
}

// ------------------------------------------------------------------ cards

interface CardEvents {
  onOpen: () => void;
  onHot: (on: boolean) => void;
  hot?: boolean;
}

/** A card's picture: the aim (or another) screenshot, a mark when it has a video. */
function CardThumb({ url, video, children }: { url?: string; video: boolean; children?: ReactNode }) {
  return (
    <span className="lc-thumb" aria-hidden="true">
      {url ? <img src={url} alt="" loading="lazy" /> : <span className="lc-thumb-blank">{children}</span>}
      {video && (
        <span className="lc-video">
          <Icon name="play" size={11} />
        </span>
      )}
    </span>
  );
}

function hotProps({ onHot }: CardEvents) {
  return {
    onPointerEnter: () => onHot(true),
    onPointerLeave: () => onHot(false),
    onFocus: () => onHot(true),
    onBlur: () => onHot(false),
  };
}

export function StuffCard({ stuff, by, members, me, ...ev }: { stuff: Stuff; by: string[]; members: LiveMember[]; me: string } & CardEvents) {
  const { t } = useTranslation();
  const places = useCallouts();
  const { from } = isMapId(stuff.map) ? places.places(stuff.map, stuff) : { from: null };
  return (
    <button type="button" className={`live-card-item${ev.hot ? " is-hot" : ""}`} onClick={ev.onOpen} {...hotProps(ev)}>
      <CardThumb url={clipPoster(stuff)} video={!!(stuff.clip || stuff.youtube)}>
        <GrenadeIcon grenade={stuff.grenade} side={stuff.side} size={34} />
      </CardThumb>
      <span className="lc-body">
        <span className="lc-title">
          <GrenadeIcon grenade={stuff.grenade} side={stuff.side} size={20} />
          <span>{stuff.title}</span>
        </span>
        <span className="lc-meta">{[from && t("live.from", { place: from }), techniqueText(t, stuff.technique)].filter(Boolean).join(" · ")}</span>
      </span>
      <WhoFaces ids={by} members={members} me={me} />
    </button>
  );
}

export function ComboCard({ combo, by, members, me, ...ev }: { combo: Combo; by: string[]; members: LiveMember[]; me: string } & CardEvents) {
  const first = combo.steps[0]?.stuff;
  return (
    <button type="button" className={`live-card-item${ev.hot ? " is-hot" : ""}`} onClick={ev.onOpen} {...hotProps(ev)}>
      <CardThumb url={first ? clipPoster(first) : undefined} video={!!(combo.clip || combo.youtube)}>
        <ComboGlyphs combo={combo} size={10} />
      </CardThumb>
      <span className="lc-body">
        <span className="lc-title">
          <ComboGlyphs combo={combo} size={8} />
          <span>{combo.title}</span>
        </span>
        <span className="lc-meta">{combo.steps.map((s) => s.stuff.title).join(" → ")}</span>
      </span>
      <WhoFaces ids={by} members={members} me={me} />
    </button>
  );
}

/** An execution with how ready it is: every pinned player in the game. */
export interface ExecRow {
  e: Execution;
  pinned: number;
  here: number;
  ready: boolean;
  mine: PlanActor | null;
}

export function execRow(e: Execution, present: Set<string>, me: string): ExecRow {
  const pinned = e.plan.players.map((p) => p.memberId).filter((x): x is string => !!x);
  const here = pinned.filter((id) => present.has(id)).length;
  return { e, pinned: pinned.length, here, ready: here === pinned.length, mine: e.plan.players.find((p) => p.memberId === me) ?? null };
}

export function ExecCard({ row, ...ev }: { row: ExecRow } & CardEvents) {
  const { t } = useTranslation();
  const { e, pinned, here, ready, mine } = row;
  return (
    <button type="button" className={`live-card-item live-exec-card${ready ? " is-ready" : ""}${ev.hot ? " is-hot" : ""}`} onClick={ev.onOpen} {...hotProps(ev)}>
      <span className="lc-thumb lc-exec-thumb" aria-hidden="true">
        <span className="lc-exec-site">{t(`areaShort.${e.target}`)}</span>
        <span className="lc-exec-kind">{t(`plan.kind.${e.kind}`)}</span>
      </span>
      <span className="lc-body">
        <span className="lc-title">
          <span>{e.title}</span>
        </span>
        <span className="lc-meta">
          <span className="live-exec-tag">{ready ? t("live.ready") : t("live.waiting")}</span> {pinned ? t("live.pinnedHere", { here, count: pinned }) : t("live.noPins")}
          {mine && isPlayer(mine) && ` · ${t("live.youAre", { slot: mine.slot, role: t(`role.${mine.role}`) })}`}
        </span>
      </span>
      <Icon name="chevronRight" />
    </button>
  );
}

export function PlantCard({ plant, me, ...ev }: { plant: PlantSpot; me: string } & CardEvents) {
  const { t } = useTranslation();
  const mine = plant.positions.find((p) => p.memberId === me);
  return (
    <button type="button" className={`live-card-item${ev.hot ? " is-hot" : ""}`} onClick={ev.onOpen} {...hotProps(ev)}>
      <CardThumb url={(plant.media.find((m) => m.caption === "planted") ?? plant.media[0])?.url} video={false}>
        <span className="c4-tag">C4</span>
      </CardThumb>
      <span className="lc-body">
        <span className="lc-title">
          <span className="c4-tag">C4</span>
          <span>{plant.name}</span>
        </span>
        <span className="lc-meta">
          {t("plant.counts", { utility: plant.utility.length, positions: plant.positions.length })}
          {mine && ` · ${t("live.yourPosition", { n: mine.slot })}`}
        </span>
      </span>
      <Icon name="chevronRight" />
    </button>
  );
}

// ------------------------------------------------------------------ views

/** The top of an open item: back to the list, previous / next, its name and a way to its own page. */
export function ViewHead({
  back,
  onBack,
  nav,
  icon,
  title,
  meta,
  open,
}: {
  back: string;
  onBack: () => void;
  nav?: { at: number; count: number; go: (dir: -1 | 1) => void };
  icon?: ReactNode;
  title: string;
  meta?: ReactNode;
  open?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="live-view-head">
      <div className="live-view-bar">
        <button type="button" className="back-link live-back" onClick={onBack}>
          <Icon name="chevronLeft" size={16} />
          {back}
        </button>
        {nav && nav.count > 1 && (
          <div className="live-nav" role="group" aria-label={t("live.browse")}>
            <button type="button" className="live-nav-btn" aria-label={t("live.previous")} title={`${t("live.previous")} (←)`} disabled={nav.at <= 0} onClick={() => nav.go(-1)}>
              <Icon name="chevronLeft" size={16} />
            </button>
            <span className="small mono">
              {nav.at + 1}/{nav.count}
            </span>
            <button type="button" className="live-nav-btn" aria-label={t("live.next")} title={`${t("live.next")} (→)`} disabled={nav.at >= nav.count - 1} onClick={() => nav.go(1)}>
              <Icon name="chevronRight" size={16} />
            </button>
          </div>
        )}
      </div>
      <div className="live-view-title">
        {icon}
        <div>
          <h2>{title}</h2>
          {meta && <p className="small live-view-meta">{meta}</p>}
        </div>
        {open}
      </div>
    </div>
  );
}

export function StuffView({ stuff, by, members, head }: { stuff: Stuff; by: string[]; members: LiveMember[]; head: Omit<Parameters<typeof ViewHead>[0], "title" | "icon" | "meta" | "open"> }) {
  const { t } = useTranslation();
  const places = useCallouts();
  const { from } = isMapId(stuff.map) ? places.places(stuff.map, stuff) : { from: null };
  const names = by.map((id) => members.find((m) => m.id === id)?.name).filter(Boolean);
  return (
    <div className="live-view">
      <ViewHead
        {...head}
        icon={<GrenadeIcon grenade={stuff.grenade} side={stuff.side} size={34} />}
        title={stuff.title}
        meta={[names.join(", "), from && t("live.from", { place: from })].filter(Boolean).join(" · ")}
        open={
          <StuffLink id={stuff.id} className="btn btn-quiet btn-small live-open">
            <Icon name="external" size={14} />
            <span className="live-open-text">{t("live.open")}</span>
          </StuffLink>
        }
      />
      <StageMedia key={stuff.id} stuff={stuff} empty={t("live.noMedia")} />
      <Technique technique={stuff.technique} />
      {stuff.notes && <p className="notes small live-notes">{stuff.notes}</p>}
    </div>
  );
}

export function ComboView({ combo, by, members, show, onShow, head }: { combo: Combo; by: string[]; members: LiveMember[]; show: ComboShow; onShow: (s: ComboShow) => void; head: Omit<Parameters<typeof ViewHead>[0], "title" | "icon" | "meta" | "open"> }) {
  const { t } = useTranslation();
  const names = by.map((id) => members.find((m) => m.id === id)?.name).filter(Boolean);
  return (
    <div className="live-view">
      <ViewHead
        {...head}
        icon={<ComboGlyphs combo={combo} size={12} />}
        title={combo.title}
        meta={[names.join(", "), t("combo.throws", { count: combo.steps.length })].filter(Boolean).join(" · ")}
        open={
          <Link to={`/combos/${combo.id}`} className="btn btn-quiet btn-small live-open">
            <Icon name="external" size={14} />
            <span className="live-open-text">{t("live.open")}</span>
          </Link>
        }
      />
      <ComboMedia combo={combo} show={show} onShow={onShow} compact stage />
      <ComboSteps combo={combo} active={typeof show === "number" ? show : null} onPick={onShow} />
      {combo.notes && <p className="notes small live-notes">{combo.notes}</p>}
    </div>
  );
}

/** The utility of an action, shown under it when it is picked: a single's stage, a combo's throws. */
function ActionMedia({ a, ctx }: { a: PlanAction; ctx: PartsContext }) {
  const { t } = useTranslation();
  const combo = a.kind === "combo" ? ctx.combos.find((c) => c.id === a.comboId) : undefined;
  const [show, setShow] = useState<ComboShow>(() => (combo ? defaultComboShow(combo) : 0));
  if (a.kind === "throw") {
    const s = ctx.stuffs.find((x) => x.id === a.stuffId);
    if (!s) return null;
    return (
      <div className="live-step-media">
        <StageMedia key={s.id} stuff={s} empty={t("live.noMedia")} />
        <Technique technique={s.technique} compact />
      </div>
    );
  }
  if (combo)
    return (
      <div className="live-step-media">
        <ComboMedia combo={combo} show={show} onShow={setShow} compact stage />
      </div>
    );
  return null;
}

/**
 * An execution on the Live page: whose part (mine first), and that part as a list
 * of steps with their times. Picking a step moves the map to that moment; a
 * throw or a combo shows its video and pictures right under it.
 */
export function ExecView({
  map,
  execution,
  row,
  me,
  actor,
  onActor,
  step,
  onStep,
  schedule,
  lookup,
  head,
}: {
  map: MapId;
  execution: Execution;
  row: ExecRow;
  me: string;
  actor: PlanActor | null;
  onActor: (id: string) => void;
  step: string | null;
  onStep: (id: string) => void;
  schedule: Schedule;
  lookup: PlanLookup;
  head: Omit<Parameters<typeof ViewHead>[0], "title" | "icon" | "meta" | "open">;
}) {
  const { t } = useTranslation();
  const { plan } = execution;
  const ctx: PartsContext = { map, plan, schedule, lookup, stuffs: execution.stuffs, combos: execution.combos, members: execution.members };
  const memberName = (id: string | null | undefined) => execution.members.find((m) => m.id === id)?.name;
  // Moves in a row as one route, like the execution's own page.
  const beats = useBeats(actor, ctx);
  return (
    <div className="live-view">
      <ViewHead
        {...head}
        title={execution.title}
        meta={
          <>
            {t(`plan.kind.${execution.kind}`)} · {t(`area.${execution.target}`)} · {planClock(plan.window.from)} → {planClock(plan.window.to)}
          </>
        }
        open={
          <Link to={`/library/${map}/executions?e=${execution.id}${actor ? `&p=${actor.id}` : ""}`} className="btn btn-quiet btn-small live-open">
            <Icon name="external" size={14} />
            <span className="live-open-text">{t("live.open")}</span>
          </Link>
        }
      />
      <p className={`live-ready small${row.ready ? " is-ready" : ""}`}>
        <span className="live-exec-tag">{row.ready ? t("live.ready") : t("live.waiting")}</span>
        {row.pinned ? t("live.pinnedHere", { here: row.here, count: row.pinned }) : t("live.noPins")}
      </p>
      <div className="live-parts" role="group" aria-label={t("live.whosePart")}>
        {plan.players.map((p) => {
          const name = memberName(p.memberId);
          return (
            <button key={p.id} type="button" className="chip live-part" aria-pressed={actor?.id === p.id} onClick={() => onActor(p.id)}>
              <span className={`pp-dot slot-${p.slot}`}>{p.slot}</span>
              {p.memberId === me ? t("live.you") : (name ?? t(`role.${p.role}`))}
            </button>
          );
        })}
      </div>
      {actor && isPlayer(actor) && (
        <section className="live-part-steps" aria-label={t("live.partOf", { n: actor.slot })}>
          <h3 className="eyebrow">
            {actor.memberId === me ? t("live.yourPart") : t("live.partOf", { n: actor.slot })} · {t(`role.${actor.role}`)} · {t(`plan.weapon.${actor.weapon}`)}
          </h3>
          {actor.actions.length === 0 ? (
            <p className="muted small">{t("plan.holdsStart")}</p>
          ) : (
            <ol className="live-steps-list">
              {beats.map((b) => {
                const on = step !== null && beatIds(b).includes(step);
                return (
                  <li key={b.key} className={`live-step${on ? " is-on" : ""}`}>
                    <button type="button" className="live-step-row" aria-pressed={on} onClick={() => onStep(b.key)}>
                      <span className="live-step-time mono">{b.start !== null ? planClock(schedule.toClock(b.start)) : "–"}</span>
                      <Icon name={beatIcon(b)} size={14} />
                      <BeatBody beat={b} ctx={ctx} />
                    </button>
                    {on && b.kind === "action" && <ActionMedia key={b.key} a={b.action} ctx={ctx} />}
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      )}
      {execution.notes && <p className="notes small live-notes">{execution.notes}</p>}
    </div>
  );
}

/** Bomb clock seconds as on the timer: 32 → "0:32". */
const bombClock = (s: number) => `0:${String(s).padStart(2, "0")}`;

/**
 * A post-plant spot while the bomb ticks: where to plant, who goes where, and
 * each post-plant throw against the real timer (in 7 s, now, done).
 */
export function PlantView({ plant, me, left, pickedUtility, onUtility, head }: { plant: PlantSpot; me: string; left: number | null; pickedUtility: number | null; onUtility: (i: number | null) => void; head: Omit<Parameters<typeof ViewHead>[0], "title" | "icon" | "meta" | "open"> }) {
  const { t } = useTranslation();
  const [pic, setPic] = useState(0);
  const pictures = plant.media;
  const viewer = useImageViewer(pictures.map((m) => ({ url: m.url, caption: `${plant.name} · ${t(`plantCaption.${m.caption}`)}` })));
  const shown = pictures[Math.min(pic, pictures.length - 1)];
  const mySlot = plant.positions.find((p) => p.memberId === me)?.slot ?? null;
  const utility = plant.utility.map((u, i) => ({ u, i })).sort((a, b) => b.u.from - a.u.from);
  return (
    <div className="live-view">
      <ViewHead {...head} icon={<span className="c4-tag">C4</span>} title={plant.name} meta={`${t("plant.kind.postplant")} · ${t(`area.${plant.site}`)}`} />
      {shown ? (
        <section className="stage-media">
          <div className="stage-main">
            <ZoomShot picture={shown} alt={t(`plantCaption.${shown.caption}`)} onFullscreen={() => viewer.open(pictures.indexOf(shown))} />
          </div>
          {pictures.length > 1 && (
            <div className="stage-thumbs" role="group" aria-label={t("media.show")}>
              {pictures.map((m, i) => (
                <button key={m.id} type="button" className="stage-thumb" aria-pressed={m === shown} aria-label={t(`plantCaption.${m.caption}`)} onClick={() => setPic(i)}>
                  <img src={m.url} alt="" loading="lazy" />
                  <span className="stage-thumb-label">{t(`plantCaption.${m.caption}`)}</span>
                </button>
              ))}
            </div>
          )}
          {viewer.viewer}
        </section>
      ) : (
        <p className="muted small media-empty">{t("detail.noPictures")}</p>
      )}
      <section>
        <h3 className="eyebrow">{t("live.afterPlantUtility")}</h3>
        {utility.length === 0 ? (
          <p className="muted small">{t("plant.noUtility")}</p>
        ) : (
          <ol className="live-steps-list">
            {utility.map(({ u, i }) => {
              const state = left === null ? null : left > u.from ? "soon" : left > u.to ? "now" : "done";
              const on = pickedUtility === i;
              return (
                <li key={i} className={`live-step${on ? " is-on" : ""}${state ? ` is-${state}` : ""}${u.slot === mySlot ? " is-mine" : ""}`}>
                  <button type="button" className="live-step-row" aria-pressed={on} onClick={() => onUtility(on ? null : i)}>
                    <span className="live-step-time mono">
                      {bombClock(u.from)}→{bombClock(u.to)}
                    </span>
                    <span className={`slot-dot slot-${u.slot}`} />
                    <GrenadeIcon grenade={u.stuff.grenade} side={u.stuff.side} size={18} />
                    <span className="live-step-text">{u.stuff.title}</span>
                    {state && (
                      <span className={`live-when is-${state}`}>
                        {state === "soon" ? t("live.inSeconds", { count: left! - u.from }) : t(`live.when.${state}`)}
                      </span>
                    )}
                  </button>
                  {on && (
                    <div className="live-step-media">
                      <StageMedia key={u.stuff.id} stuff={u.stuff} empty={t("live.noMedia")} />
                      <Technique technique={u.stuff.technique} compact />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
      {plant.positions.length > 0 && (
        <section>
          <h3 className="eyebrow">{t("plant.positions")}</h3>
          <ul className="roles">
            {plant.positions.map((p, i) => (
              <li key={i} className={p.memberId === me ? "is-mine" : undefined}>
                <span className={`slot-dot slot-${p.slot}`} />
                <span className="roles-role">{p.label || t("plant.playerN", { n: p.slot })}</span>
                {p.member ? (
                  <span className="pp-member small">
                    <Avatar name={p.member.name} url={p.member.avatarUrl} size={18} />
                    {p.memberId === me ? t("live.you") : p.member.name}
                  </span>
                ) : (
                  <span className="muted small">{t("plant.playerN", { n: p.slot })}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {plant.notes && <p className="notes small live-notes">{plant.notes}</p>}
    </div>
  );
}

/** The bomb is down: the timer, and which site (the game does not say). */
export function BombStrip({
  left,
  site,
  onSite,
  away,
  onAway,
}: {
  left: number;
  site: Site | null;
  onSite: (s: Site | null) => void;
  away: boolean;
  onAway: (away: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={`live-bomb${left <= 10 ? " is-late" : ""}`} role="status">
      <span className="c4-tag">C4</span>
      <b className="mono live-bomb-time">{bombClock(left)}</b>
      {site ? (
        <>
          <span className="live-bomb-text">{t("live.bombOn", { site: t(`area.${site}`) })}</span>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => onSite(null)}>
            {t("live.changeSite")}
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => onAway(!away)}>
            {away ? t("live.showPlan") : t("live.backToUtility")}
          </button>
        </>
      ) : (
        <>
          <span className="live-bomb-text">{t("live.whichSite")}</span>
          <div className="live-bomb-sites" role="group" aria-label={t("live.whichSite")}>
            {(["a", "b"] as const).map((s) => (
              <button key={s} type="button" className="btn btn-primary btn-small" onClick={() => onSite(s)}>
                {t(`areaShort.${s}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
